import React, { PureComponent, Fragment } from 'react';
import { Script } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import searchPurple from '../../../assets/images/icons/searchPurple.svg';
import Link from '../../base/Link';
import { getKey, isValidArray, exists } from '../../../helpers';
import Questions from '../../base/Questions';
import CategoryFaq from '../../base/CategoryFaq';
import Text from '../../base/Text';
import Styles from './Faq.styles';
import PlusIcon from '../../base/Questions/PlusIcon';
import Helmet from 'react-helmet';

const Wrapper = styled.div`${Styles.wrapper}`; // prettier-ignore
const SearchWrapper = styled.div`${Styles.searchWrapper}`; // prettier-ignore
const SearchInput = styled.input.attrs({ className: 'cbx-bold' })`${Styles.searchInput}`; // prettier-ignore
const SearchInputWrapper = styled.div`${Styles.searchInputWrapper}`; // prettier-ignore
const SearchIcon = styled.img.attrs({ src: searchPurple })`${Styles.searchIcon}`; // prettier-ignore
const SearchText = styled(Text).attrs({ bold: true })`${Styles.searchText}`; // prettier-ignore
const CategoryTitle = styled(Text).attrs({ bold: true })`${Styles.categoryTitle}`; // prettier-ignore
const CategoryIcon = styled.img`${Styles.categoryIcon};`; // prettier-ignore
const CollapseTitle = styled(Text).attrs({ bold: true })`${Styles.colapseTitle}`; // prettier-ignore
const CollapseWrapper = styled.div`${Styles.collapseWrapper}`; // prettier-ignore
const NoResults = styled.div.attrs({ className: 'cbx-bold' })`${Styles.noResults}`; // prettier-ignore
const SeeMoreQuestions = styled(Link).attrs({ className: 'cbx-bold' })`${Styles.seeMoreQuestions}`; // prettier-ignore

/**
 * Faq component class
 */
class Faq extends PureComponent {
  /**
   * Props
   * @property {boolean} hasSearch - true to display the search bar
   * @property {string} searchPlaceHolder - message to display when the search is not active
   * @property {string} noResults - message to display when te search returns null
   * @property {string} seeMoreQuestions - when there's is not search, text at the bottom
   * @property {string} seeMoreQuestionsLink - Link to faq section, relative slug
   * @property {Array} categoryQuestions - array with questions for each category
   */
  static propTypes = {
    hasSearch: PropTypes.bool,
    translations: PropTypes.object,
    searchPlaceHolder: PropTypes.string,
    noResults: PropTypes.string,
    seeMoreQuestions: PropTypes.string,
    seeMoreQuestionsLink: PropTypes.string,
    open: PropTypes.bool,
    categoryQuestions: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        category: PropTypes.string,
        icon: PropTypes.string,
        open: PropTypes.bool,
        questions: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string,
            question: PropTypes.string,
            answer: PropTypes.string
          })
        )
      })
    )
  };

  /**
   * Highlight the search results
   * @param {Array} searchQueries - words to search
   * @param {string} sentence - sentence
   * @returns {string}
   */
  static hightlightString(searchQueries, sentence) {
    const charReplace = {
      a: 'á',
      e: 'é',
      i: 'í',
      o: 'ó',
      u: 'ú',
      n: 'ñ'
    };

    const escapedChar = Object.keys(charReplace)
      .map(char => char)
      .join('|');

    const escapedWords = searchQueries.map((word) => {
      return word.replace(new RegExp(escapedChar, 'gi'), w => `[${w}${charReplace[w]}]`);
    });
    const replace = escapedWords.join('|');
    const regex = new RegExp(`([^&]|^)(${replace})(?![^<>]*>|[^&]*;)`, 'gi');  
    return sentence.replace(regex, '$1<span class="searchHighlight">$2</span>');
  }

  /**
   * Constructor method
   * @param {*} props - component props
   */
  constructor(props) {
    super(props);
    this.onSearch = this.onSearch.bind(this);
    this.expandAction = this.expandAction.bind(this);
    this.collapseAction = this.collapseAction.bind(this);
    this.state = {
      isSearchActive: [],
      isCategoryOpen: props.open,
      updatedComponent: true
    };
  }

  /**
   * Search method
   * @param {Object} event - synthethic event
   */
  onSearch(event) {
    const searchValue = event.target.value;
    const search = searchValue && searchValue.replace(/[^0-9a-zA-Záéíóúñ]/gi, ' ');

    const { isSearchActive: currentSearchValue } = this.state;
    const searchValues = search ? search.split(' ').filter(result => exists(result)) : [];
    const areValuesEqual = isValidArray(currentSearchValue)
      && isValidArray(searchValues)
      && searchValues.every(e => currentSearchValue.includes(e));

    if (!areValuesEqual) {
      this.setState({
        isSearchActive: searchValues
      });
    }

    if (search.length <= 0) {
      this.setState({
        isSearchActive: null
      });
    }
  }

  /**
   * Renders the search input
   * @returns {?JSX}
   */
  getSearch() {
    const { hasSearch, searchPlaceHolder, translations } = this.props;
              
    if (exists(hasSearch)) {
      return (
        <SearchWrapper>
          <SearchText>{translations.whatCanWeHelpYouFind}</SearchText>
          <SearchInputWrapper>
            <SearchInput
              placeholder={translations.search}
              type="search"
              autoComplete="off"
              onChange={this.onSearch}
            />
            <SearchIcon />
          </SearchInputWrapper>
        </SearchWrapper>
      );
    }

    return null;
  }

  /**
   * Renders results
   * @returns {?JSX}
   */
  getResults() {
    const { categoryQuestions: questions, noResults } = this.props;
    const { isSearchActive: searchQueries } = this.state;
    const categoryQuestions = JSON.parse(JSON.stringify(questions));

    /**
     * Search method
     * @param {string} sentence - sentence to check
     * @param {*} word - word to look forward
     * @returns {boolean}
     */
    const hasWord = (sentence, word) => sentence
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .includes(
        word
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      );

    const results = categoryQuestions
      .map((datum) => {
        const queryResult = datum.questions.filter((item) => {
          const hasWordInQuestion = searchQueries
            .map(searchQuery => hasWord(item.question, searchQuery))
            .some(e => e);
          const hasWordInAnswer = searchQueries
            .map(searchQuery => hasWord(item.answer, searchQuery))
            .some(e => e);

          if (hasWordInQuestion) {
            Object.assign(item, {
              question: this.constructor.hightlightString(searchQueries, item.question)
            });
          }

          if (hasWordInAnswer) {
            Object.assign(item, {
              answer: this.constructor.hightlightString(searchQueries, item.answer)
            });
          }

          return hasWordInQuestion || hasWordInAnswer;
        });

        if (isValidArray(queryResult)) {
          const { key, category, icon } = datum;

          return this.getQuestions([
            {
              key,
              category,
              icon,
              questions: queryResult,
              open: true
            }
          ]);
        }

        return null;
      })
      .filter(result => exists(result));

    return isValidArray(results) ? results : <NoResults>{noResults}</NoResults>;
  }

  expandAction() {
    this.setState(({updatedComponent}) => ({
      isCategoryOpen: true,
      updatedComponent: !updatedComponent
    }));
  }

  collapseAction() {
    this.setState(({updatedComponent}) => ({
      isCategoryOpen: false,
      updatedComponent: !updatedComponent
    }));
  }

  /**
   * Renders questions and answers
   * @param {array} categoryQuestions  - questions categories
   * @returns {?JSX}
   */
   getQuestions = categoryQuestions => categoryQuestions.map((datum, index) => {
    const {
      key, category, questions, open, icon
    } = datum;

    return (
      <Fragment key={key || `question${index}`}>
        {exists(category) && (
        <CategoryTitle hasIcon={exists(icon)}>
          {exists(icon) && <CategoryIcon src={icon} />}
          {category}
        </CategoryTitle>
        )}
        {isValidArray(questions)
            && questions.map(item => (
              <Questions
                key={getKey(item, 'key')}
                question={getKey(item, 'question')}
                answer={getKey(item, 'answer')}
                open={open}
              />
            ))}
      </Fragment>
    );
  });

  /**
   * Renders categories with questions
   * @param {array} categories  - questions categories
   * @returns {?JSX}
   */
  getCategories = categories => categories.map((datum, index) => {
    const {
      key, category, questions, open, icon
    } = datum;
    const {
      state : {isCategoryOpen}
    } = this;
    return (
    <Fragment key={key || `question${index}`}>
      <CategoryFaq key={key} category={category} questions={questions} open={isCategoryOpen} icon={icon} />
      </Fragment>
    );
  });

  /**
   * Render, collapse and expand categories
   * @returns {?JSX}
   */
  renderCategories() {
    const { 
      props: { categoryQuestions, translations},
      state: { updatedComponent}
    } = this;
    return <Wrapper>
      <CollapseWrapper>
        <Wrapper onClick={this.expandAction}>
          <CollapseTitle>
            {"+ " + translations.expand}
          </CollapseTitle>
        </Wrapper>
        <Wrapper onClick={this.collapseAction}>
          <CollapseTitle>
            {"- " + translations.collapse}
          </CollapseTitle>
        </Wrapper>
      </CollapseWrapper>
      {updatedComponent && this.getCategories(categoryQuestions)}
      {!updatedComponent && this.getCategories(categoryQuestions)}
    </Wrapper>;
  }

  getAllQuestions() {
    const {
      props: {categoryQuestions, translations}
    } = this;
    const allCategoryQuestions = categoryQuestions.map((category) => category.questions);
    const allQuestions = Array.prototype.concat.apply([], allCategoryQuestions);
    return allQuestions;
  }

  getFAQSchema() {
    const allQuestions = this.getAllQuestions();
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": allQuestions.map((question) => ({
        "@type": "Question",
        "name": question.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": question.answer
        }
      }))
    }
    return JSON.stringify(schema);
  }

  /**
   * render method
   * @returns {?JSX}
   */
  render() {
    const {
      props: {
        categoryQuestions, hasSearch, seeMoreQuestions, seeMoreQuestionsLink
      },
      state: { isSearchActive }
    } = this;
    return (
      <div className="container">
        <Helmet>
          <script type="application/ld+json">
            {this.getFAQSchema()}
          </script>
        </Helmet>
        <div className="row">
          <Wrapper className="col-12">
            {this.getSearch()}
            {!isValidArray(isSearchActive) && this.renderCategories()}
            {isValidArray(isSearchActive) && this.getResults()}
            {!exists(hasSearch)
              && exists(seeMoreQuestions) && (
                <SeeMoreQuestions href={seeMoreQuestionsLink}>{seeMoreQuestions}</SeeMoreQuestions>
            )}
          </Wrapper>
        </div>
      </div>
    );
  }
}

export default Faq;